'use client'

import { cn } from '@/lib/utils'
import { CrucibleMark } from './crucible-mark'
import { CrucibleBadge, type BadgeVariant } from './crucible-badge'

/**
 * The one brand block every header renders: the Crucible mark beside the
 * CRUCIBLE wordmark. Before this existed, the same tile-and-text pair was
 * hand-rolled in every shell, which is how a logo change became an
 * eight-site hunt. Add a variant here rather than a new inline block.
 *
 * Wordmark styling per variant is deliberately the styling each surface
 * already had; the brand file's typography is not applied here.
 *
 * Every lockup renders the mark as the rounded-square badge (the brand file's
 * "Crucible-icon-square"). The bare mark is now only used where it stands alone
 * at size: the animated loop in the auth hero, and the small monochrome mark on
 * the persona tiles.
 */

export type LockupVariant =
  /** Desktop workspace header on the kitchen-sink shell (DM Sans display). */
  | 'shell'
  /** Desktop header on legacy shells (Audrey, Audrey Trail, test cases). */
  | 'legacy-shell'
  /** Public landing header and footer. */
  | 'landing'
  /** Login and pending-approval brand block below lg. */
  | 'auth-mobile'

/**
 * Each tracked wordmark carries a negative inline-end margin equal to its
 * tracking. CSS adds letter-spacing after the final character too, so without
 * it a centred wordmark renders half the tracking left of centre — measured at
 * 7.5px on the landing hero's 15px tracking, against the mark directly above.
 */
const VARIANTS: Record<LockupVariant, { mark: string; wordmark: string; gap: string; badge?: BadgeVariant }> = {
  shell: {
    mark: 'h-11',
    badge: 'amber',
    wordmark: 'text-display tracking-tight text-[var(--text-primary)]',
    gap: 'gap-0',
  },
  'legacy-shell': {
    mark: 'h-11',
    badge: 'amber',
    wordmark: 'text-3xl font-bold tracking-tight text-foreground',
    gap: 'gap-0',
  },
  landing: {
    mark: 'h-9',
    badge: 'amber',
    wordmark: 'text-[15px] font-semibold tracking-[2px] -me-[2px] text-foreground',
    gap: 'gap-2.5 md:gap-3',
  },
  'auth-mobile': {
    mark: 'h-14',
    badge: 'amber',
    wordmark: 'text-[18px] font-extrabold tracking-[3px] -me-[3px] text-foreground',
    gap: 'gap-3',
  },
}

interface CrucibleLockupProps {
  variant: LockupVariant
  /** Stack the wordmark under the mark instead of beside it. */
  stacked?: boolean
  className?: string
  /** Override the mark's height class, e.g. for a tighter header. */
  markClassName?: string
  /**
   * Width of the column the mark is centred in, for shells that align the
   * wordmark to the sidebar edge. An inline style so the value can come
   * from the rail constant.
   */
  markColumnWidth?: number
}

export function CrucibleLockup({
  variant,
  stacked = false,
  className,
  markClassName,
  markColumnWidth,
}: CrucibleLockupProps) {
  const v = VARIANTS[variant]
  const mark = v.badge ? (
    <CrucibleBadge variant={v.badge} className={cn(v.mark, markClassName)} title="" />
  ) : (
    <CrucibleMark className={cn(v.mark, markClassName)} title="" />
  )
  return (
    <span
      role="img"
      aria-label="Crucible"
      className={cn('inline-flex items-center', stacked ? 'flex-col' : 'flex-row', v.gap, className)}
    >
      {markColumnWidth ? (
        <span className="flex shrink-0 items-center justify-center" style={{ width: markColumnWidth }}>
          {mark}
        </span>
      ) : (
        mark
      )}
      <span className={v.wordmark}>CRUCIBLE</span>
    </span>
  )
}
